<template>
	<div v-if="isLoading" :aria-label="$t('spa__general.loading')">
		<SuiSkeleton height="5rem" width="100%" />
	</div>
	<div v-else class="task-progress">
		<span
			v-if="!isDetailed"
			class="task-progress__percentage"
			:style="percentageStyle"
			:class="{
				'task-progress__percentage--is-overtime': isOverTime,
			}"
		>
			{{ `${progressPercentage}%` }}
		</span>

		<div class="task-progress__bar-container">
			<div class="task-progress__bar">
				<div
					v-if="isDetailed"
					class="task-progress__details task-progress__details--top"
				>
					<span>
						<strong>{{ getLabelValue(durationDone) }}</strong>
						{{ $t('task_progress_done') }}
					</span>
					<span v-if="durationScheduled">
						{{ $t('timebilling_todo') }}
						<strong>{{ getLabelValue(durationScheduled) }}</strong>
					</span>
				</div>

				<SuiProgressBar size="compact">
					<SuiProgressBarArea
						v-for="progress in progressValues"
						:key="progress.name"
						v-tooltip="progress.label"
						:percentage="progress.percent"
						:class="[
							'task-progress__bar-item',
							`task-progress__bar-item--${progress.class}`,
						]"
						:aria-label="progress.label"
					/>
				</SuiProgressBar>

				<div
					v-if="isDetailed"
					class="task-progress__details task-progress__details--bottom"
				>
					<span>
						<strong>{{ getLabelValue(durationPlanned) }}</strong>
						{{ $t('task_progress_estimated') }}
					</span>
					<span v-if="overScheduledTime + overTimeTime">
						{{ $t('task_progress_overtime') }}
						<strong>
							{{
								getLabelValue(overScheduledTime + overTimeTime)
							}}
						</strong>
					</span>
				</div>
			</div>

			<div v-if="isDetailed" class="task-progress__bar-percentage">
				<strong>{{ `${progressPercentage}%` }}</strong>
				<br />
				{{ $t('task_progress_done') }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	SuiProgressBar,
	SuiProgressBarArea,
	SuiSkeleton,
} from '@developers/scoro-ui';
import { type Task } from '@/generated/graphql';
import { computed } from 'vue';
import { useFormatter, useTranslation } from '@/composables';

export type TaskProgressProps = {
	durationPlanned: Task['durationPlanned'];
	durationDone: Task['durationDone'];
	durationRemaining: Task['durationRemaining'];
	durationScheduled: Task['durationScheduled'];
	progressPercentage: Task['progressPercentage'];
	isDetailed?: boolean;
	isLoading?: boolean;
};

const props = defineProps<TaskProgressProps>();

const { formatLongDuration } = useFormatter();
const { t } = useTranslation();

const actualPlannedTime = computed(
	() => props.durationScheduled + props.durationDone
);

const isOverTime = computed(() => props.durationDone > props.durationPlanned);

const overTimeTime = computed(() =>
	isOverTime.value ? props.durationDone - props.durationPlanned : 0
);

const isOverScheduled = computed(
	() => actualPlannedTime.value > props.durationPlanned
);

const overScheduledTime = computed(() => {
	return isOverScheduled.value
		? actualPlannedTime.value - props.durationPlanned - overTimeTime.value
		: 0;
});

const scheduledTime = computed(() => {
	if (isOverTime.value) {
		return 0;
	}

	return isOverScheduled.value
		? props.durationPlanned - props.durationDone
		: props.durationScheduled;
});

const doneTime = computed(() => {
	if (isOverScheduled.value || isOverTime.value) {
		return props.durationDone <= props.durationPlanned
			? props.durationDone
			: 0;
	}

	return props.durationDone;
});

const bufferTime = computed(() =>
	isOverTime.value || isOverScheduled.value ? 0 : props.durationRemaining
);

const totalTime = computed(() =>
	isOverTime.value || isOverScheduled.value
		? actualPlannedTime.value
		: props.durationPlanned
);

const estimatedTime = computed(() =>
	isOverTime.value ? props.durationPlanned : 0
);

const getPercentage = (value: number) => {
	if (value) {
		return Math.round((value / totalTime.value) * 100);
	} else {
		return 0;
	}
};

const getLabelValue = (value: number) => {
	return formatLongDuration({
		duration: value,
		removeMinutesOnFullHours: true,
	});
};

const progressValues = computed(() => {
	const result = [
		{
			name: 'done',
			label: `${t('task_progress_done')}: ${getLabelValue(
				doneTime.value
			)}`,
			percent: getPercentage(doneTime.value),
			class: 'done',
		},
		{
			name: 'estimated',
			label: `${t('task_progress_estimated')}: ${getLabelValue(
				estimatedTime.value
			)}`,
			percent: getPercentage(estimatedTime.value),
			class: 'done',
		},
		{
			name: 'scheduled',
			label: `${t('task_progress_scheduled')}: ${getLabelValue(
				scheduledTime.value
			)}`,
			percent: getPercentage(scheduledTime.value),
			class: 'scheduled',
		},
		{
			name: 'overTime',
			label: `${t('task_progress_overtime')}: ${getLabelValue(
				overTimeTime.value
			)}`,
			percent: getPercentage(overTimeTime.value),
			class: 'over-time',
		},
		{
			name: 'overScheduled',
			label: `${t('task_progress_overscheduled')}: ${getLabelValue(
				overScheduledTime.value
			)}`,
			percent: getPercentage(overScheduledTime.value),
			class: 'over-scheduled',
		},
		{
			name: 'buffer',
			label: `${t('task_progress_buffer')}: ${getLabelValue(
				bufferTime.value
			)}`,
			percent: getPercentage(bufferTime.value),
			class: 'buffer',
		},
	];

	return result.filter((process) => process.percent);
});

const percentageStyle = computed(() => {
	return props.progressPercentage > 20
		? `left:${props.progressPercentage}%; transform: translateX(-100%);`
		: '';
});
</script>

<style lang="scss" scoped>
$progress-bar-line-angle: -45deg;
$progress-bar-line-width: 1px;
$progress-bar-gap-width: 1.828px;

.task-progress {
	position: relative;

	&__percentage {
		font-size: 1.375rem;
		color: $blue-70;
		font-weight: $font-weight-bold;
		position: absolute;
		left: 0;
		top: -2.25rem;
		white-space: nowrap;

		&--is-overtime {
			color: $red-50;
		}
	}

	&__bar-container {
		display: flex;
		align-items: center;
		gap: 2rem;
	}

	&__bar {
		flex: 1;

		&-item {
			&--done {
				background-color: $blue-50;
			}

			&--over-time {
				background-color: $red-50;
			}

			&--scheduled {
				@include background-line-pattern(
					$progress-bar-line-angle,
					$blue-40,
					$progress-bar-line-width,
					$progress-bar-gap-width
				);
			}

			&--over-scheduled {
				@include background-line-pattern(
					$progress-bar-line-angle,
					$red-40,
					$progress-bar-line-width,
					$progress-bar-gap-width
				);
			}

			&--buffer {
				background-color: $neutral-20;
			}
		}
	}

	&__bar-percentage {
		color: $blue-70;
		text-align: center;
		line-height: 2rem;

		strong {
			font-size: 1.625rem;
			color: $neutral-90;
		}
	}

	&__details {
		color: $neutral-70;
		display: flex;
		justify-content: space-between;

		&--top {
			margin-bottom: 0.5rem;
		}

		&--bottom {
			margin-top: 0.5rem;
		}

		strong {
			color: $neutral-90;
		}
	}
}
</style>
